// The Product Knowledge Base: one verified fact per row, managed from
// /admin/settings. The vocabularies live in enums.ts (checked against the Prisma
// schema); here are the labels and shapes the UI reads. Values are never shown raw.

import type { KbAudience, KbCategory, KbSection, KbStatus } from './enums';

export const KB_SECTIONS: readonly KbSection[] = [
  'PRODUCT_INVENTORY',
  'WORKFLOWS',
  'DETECTION_ENFORCEMENT',
  'PLATFORM_COVERAGE',
  'METRICS_CLAIMS',
  'PRICING_BILLING',
  'SUPPORT_OPERATIONS',
  'PRIVACY_SECURITY_LEGAL',
  'REPUTATION_PARTNERSHIPS',
];

export const KB_CATEGORIES: readonly KbCategory[] = [
  'FEATURE',
  'SCENARIO',
  'PLATFORM',
  'CAPABILITY',
  'PLAN_LIMIT',
  'METRIC_CLAIM',
  'DATA',
  'LIMITATION',
];

export const KB_AUDIENCES: readonly KbAudience[] = ['PUBLIC', 'INTERNAL'];

export const KB_STATUSES: readonly KbStatus[] = ['ACTIVE', 'PLANNED', 'DEPRECATED'];

export const KB_SECTION_LABEL: Readonly<Record<KbSection, string>> = {
  PRODUCT_INVENTORY: 'Product inventory',
  WORKFLOWS: 'Workflows',
  DETECTION_ENFORCEMENT: 'Detection & enforcement',
  PLATFORM_COVERAGE: 'Platform coverage',
  METRICS_CLAIMS: 'Metrics & claims',
  PRICING_BILLING: 'Pricing & billing',
  SUPPORT_OPERATIONS: 'Support operations',
  PRIVACY_SECURITY_LEGAL: 'Privacy, security & legal',
  REPUTATION_PARTNERSHIPS: 'Reputation & partnerships',
};

export const KB_CATEGORY_LABEL: Readonly<Record<KbCategory, string>> = {
  FEATURE: 'Feature',
  SCENARIO: 'Scenario',
  PLATFORM: 'Platform',
  CAPABILITY: 'Capability',
  PLAN_LIMIT: 'Plan / limit',
  METRIC_CLAIM: 'Metric / claim',
  DATA: 'Data',
  LIMITATION: 'Limitation',
};

export const KB_AUDIENCE_LABEL: Readonly<Record<KbAudience, string>> = {
  PUBLIC: 'Public',
  INTERNAL: 'Internal',
};

export const KB_STATUS_LABEL: Readonly<Record<KbStatus, string>> = {
  ACTIVE: 'Active',
  PLANNED: 'Planned',
  DEPRECATED: 'Deprecated',
};

/** One fact as the API returns it; dates are ISO strings over the wire. */
export interface KbFact {
  id: string;
  /** The row id from Cloud's KB sheet (FACT-001 …), if it came from there. */
  factId: string | null;
  section: KbSection;
  category: KbCategory;
  audience: KbAudience;
  status: KbStatus;
  title: string;
  body: string;
  /** What to gather for this fact (the sheet's What_To_Collect). */
  whatToCollect: string | null;
  source: string | null;
  owner: string | null;
  approver: string | null;
  /** A service-key name whose live value this fact quotes, if any. */
  liveKey: string | null;
  changeNote: string | null;
  verifiedAt: string | null;
  sortOrder: number;
  createdAt: string;
  updatedAt: string;
}

/** What the admin sends to create or replace a fact. */
export interface KbFactInput {
  factId?: string | null;
  section: KbSection;
  category: KbCategory;
  audience: KbAudience;
  status: KbStatus;
  title: string;
  body: string;
  whatToCollect?: string | null;
  source?: string | null;
  owner?: string | null;
  approver?: string | null;
  liveKey?: string | null;
  changeNote?: string | null;
  verifiedAt?: string | null;
  sortOrder?: number;
}
